import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function IlluAttribution() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const ctx = gsap.context(() => {
            const badge30d   = svg.querySelector('#at-30d');
            const headerText = svg.querySelector('#at-header-text');
            const trackBg    = svg.querySelector('#at-track-bg');
            const trackFill  = svg.querySelector('#at-track-fill');
            const day1       = svg.querySelector('#at-day1');
            const day7       = svg.querySelector('#at-day7');
            const popup      = svg.querySelector('#at-popup');
            const convertDot = svg.querySelector('#at-convert-dot');
            const day30      = svg.querySelector('#at-day30');
            const strip      = svg.querySelector('#at-strip');
            const pulse1     = svg.querySelector('#at-pulse1');
            const pulse2     = svg.querySelector('#at-pulse2');

            // ── initial states ──
            gsap.set(badge30d,   { opacity: 0, scale: 0, svgOrigin: '59 44' });
            gsap.set(headerText, { opacity: 0, x: 12 });
            gsap.set(trackBg,    { opacity: 0 });
            gsap.set(trackFill,  { scaleX: 0, svgOrigin: '68 150' });
            gsap.set(day1,       { opacity: 0, y: 10 });
            gsap.set(day7,       { opacity: 0, y: 10 });
            gsap.set(convertDot, { opacity: 0, scale: 0, svgOrigin: '306 150' });
            gsap.set(popup,      { opacity: 0, y: -14 });
            gsap.set(day30,      { opacity: 0, y: 10 });
            gsap.set(strip,      { opacity: 0, y: 10 });

            // ── entry timeline ──
            const tl = gsap.timeline({
                scrollTrigger: { trigger: svg, start: 'top 85%', once: true },
                onComplete: startLoops,
            });

            tl
                .to(badge30d,   { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(2.2)' })
                .to(headerText, { opacity: 1, x: 0, duration: 0.45, ease: 'power2.out' }, '-=0.25')
                .to(trackBg,    { opacity: 1, duration: 0.3 }, 0.4)
                .to(trackFill,  { scaleX: 1, duration: 1.1, ease: 'power2.inOut' }, 0.45)
                .to(day1,       { opacity: 1, y: 0, duration: 0.38, ease: 'power2.out' }, 0.5)
                .to(day7,       { opacity: 1, y: 0, duration: 0.38, ease: 'power2.out' }, 0.72)
                .to(convertDot, { opacity: 1, scale: 1, duration: 0.55, ease: 'back.out(2.5)' }, 1.05)
                .to(popup,      { opacity: 1, y: 0, duration: 0.4,  ease: 'back.out(1.8)' },  1.18)
                .to(day30,      { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }, 1.3)
                .to(strip,      { opacity: 1, y: 0, duration: 0.4,  ease: 'power2.out' }, 1.25);

            // ── loop animations ──
            function startLoops() {
                // Double concentric pulse rings on the convert dot
                gsap.fromTo(pulse1,
                    { attr: { r: 12 }, opacity: 0.5 },
                    { attr: { r: 30 }, opacity: 0, duration: 1.5, ease: 'sine.out', repeat: -1, repeatDelay: 0.9 }
                );
                gsap.fromTo(pulse2,
                    { attr: { r: 12 }, opacity: 0.3 },
                    { attr: { r: 38 }, opacity: 0, duration: 1.9, ease: 'sine.out', repeat: -1, repeatDelay: 0.9, delay: 0.6 }
                );

                // 30d badge breathes
                gsap.to(badge30d, {
                    scale: 1.06, svgOrigin: '59 44',
                    duration: 1.9, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });

                // Track fill subtle opacity breathe
                gsap.to(trackFill, {
                    opacity: 0.75, duration: 2.4, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });
            }
        }, svgRef);

        return () => ctx.revert();
    }, []);

    return (
        <svg ref={svgRef} viewBox="0 0 460 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
            <defs>
                <linearGradient id="at-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#f0eaff"/><stop offset="1" stopColor="#e6deff"/>
                </linearGradient>
                <linearGradient id="at-fill" x1="0" y1="0" x2="1" y2="0">
                    <stop stopColor="#7c3aed"/><stop offset="1" stopColor="#a78bfa"/>
                </linearGradient>
                <linearGradient id="at-badge-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#7c3aed"/><stop offset="1" stopColor="#6d28d9"/>
                </linearGradient>
                <filter id="at-shadow" x="-5%" y="-10%" width="110%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="#4c1d95" floodOpacity="0.09"/>
                </filter>
                <filter id="at-glow" x="-40%" y="-40%" width="180%" height="180%">
                    <feDropShadow dx="0" dy="0" stdDeviation="7" floodColor="#7c3aed" floodOpacity="0.55"/>
                </filter>
                <filter id="at-callout-shadow" x="-10%" y="-20%" width="120%" height="150%">
                    <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="#7c3aed" floodOpacity="0.25"/>
                </filter>
                <clipPath id="at-card-clip">
                    <rect x="18" y="14" width="424" height="252" rx="18"/>
                </clipPath>
            </defs>

            <rect width="460" height="280" fill="url(#at-bg)"/>
            {/* Shadow outside clip so it stays visible */}
            <rect x="18" y="14" width="424" height="252" rx="18" fill="#fff" filter="url(#at-shadow)"/>

            <g clipPath="url(#at-card-clip)">

                {/* ── Header ── */}
                <g id="at-30d">
                    <rect x="28" y="22" width="62" height="44" rx="22" fill="url(#at-badge-grad)"/>
                    <text x="59" y="50" textAnchor="middle" fontSize="18" fill="#fff" fontWeight="900" fontFamily="inherit">30d</text>
                </g>

                <g id="at-header-text">
                    <text x="104" y="40" fontSize="13" fontWeight="700" fill="#1a1535" fontFamily="inherit">Cookie window</text>
                    <text x="104" y="58" fontSize="10" fill="#9d8ec4" fontFamily="inherit">Every conversion in this window earns you commission</text>
                    <line x1="28" y1="76" x2="432" y2="76" stroke="#f0ecfc" strokeWidth="1"/>
                </g>

                {/* ── Timeline track ── */}
                <rect id="at-track-bg" x="68" y="145" width="356" height="10" rx="5" fill="#ede9fe"/>
                <rect id="at-track-fill" x="68" y="145" width="238" height="10" rx="5" fill="url(#at-fill)"/>

                {/* ── Day 1 ── */}
                <g id="at-day1">
                    <rect x="44" y="88" width="48" height="20" rx="10" fill="#ede9fe"/>
                    <text x="68" y="102" textAnchor="middle" fontSize="9" fill="#7c3aed" fontWeight="600" fontFamily="inherit">Day 1</text>
                    <line x1="68" y1="108" x2="68" y2="141" stroke="#c4b5fd" strokeWidth="1.5" strokeDasharray="3 2"/>
                    <circle cx="68" cy="150" r="9" fill="#fff" stroke="#7c3aed" strokeWidth="2.5"/>
                    <circle cx="68" cy="150" r="3.5" fill="#7c3aed"/>
                    <text x="68" y="176" textAnchor="middle" fontSize="9.5" fill="#a78bfa" fontFamily="inherit">Link clicked</text>
                </g>

                {/* ── Day 7 ── */}
                <g id="at-day7">
                    <rect x="156" y="88" width="48" height="20" rx="10" fill="#ede9fe"/>
                    <text x="180" y="102" textAnchor="middle" fontSize="9" fill="#7c3aed" fontWeight="600" fontFamily="inherit">Day 7</text>
                    <line x1="180" y1="108" x2="180" y2="141" stroke="#c4b5fd" strokeWidth="1.5" strokeDasharray="3 2"/>
                    <circle cx="180" cy="150" r="9" fill="#fff" stroke="#7c3aed" strokeWidth="2.5"/>
                    <circle cx="180" cy="150" r="3.5" fill="#7c3aed"/>
                    <text x="180" y="176" textAnchor="middle" fontSize="9.5" fill="#a78bfa" fontFamily="inherit">Page visited</text>
                </g>

                {/* ── Day 23 — callout popup ── */}
                <g id="at-popup">
                    <text x="306" y="88" textAnchor="middle" fontSize="9" fill="#7c3aed" fontWeight="600" fontFamily="inherit">Day 23</text>
                    <rect x="248" y="94" width="116" height="32" rx="16" fill="#7c3aed" filter="url(#at-callout-shadow)"/>
                    <text x="306" y="115" textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700" fontFamily="inherit">Converts! 🎉</text>
                    <line x1="306" y1="126" x2="306" y2="136" stroke="#7c3aed" strokeWidth="2"/>
                    <polygon points="300,134 312,134 306,141" fill="#7c3aed"/>
                </g>

                {/* ── Day 23 — convert dot with pulse rings ── */}
                <g id="at-convert-dot">
                    {/* Pulse rings — r-based animation avoids square bounding box */}
                    <circle id="at-pulse1" cx="306" cy="150" r="12" fill="#7c3aed" opacity="0"/>
                    <circle id="at-pulse2" cx="306" cy="150" r="12" fill="#7c3aed" opacity="0"/>
                    <circle cx="306" cy="150" r="18" fill="#7c3aed" opacity="0.12"/>
                    <circle cx="306" cy="150" r="12" fill="#7c3aed" filter="url(#at-glow)"/>
                    <circle cx="306" cy="150" r="5" fill="#fff"/>
                </g>

                {/* ── Day 30 — cutoff ── */}
                <g id="at-day30">
                    <rect x="386" y="88" width="48" height="20" rx="10" fill="#f3f4f6"/>
                    <text x="410" y="102" textAnchor="middle" fontSize="9" fill="#9ca3af" fontWeight="600" fontFamily="inherit">Day 30</text>
                    <line x1="410" y1="108" x2="410" y2="141" stroke="#e5e7eb" strokeWidth="1.5" strokeDasharray="3 2"/>
                    <circle cx="410" cy="150" r="9" fill="#f9fafb" stroke="#d1d5db" strokeWidth="2"/>
                    <text x="410" y="176" textAnchor="middle" fontSize="9.5" fill="#d1d5db" fontFamily="inherit">Cookie end</text>
                </g>

                {/* ── Bottom info strip ── */}
                <g id="at-strip">
                    <rect x="34" y="200" width="392" height="36" rx="12" fill="#f5f0ff"/>
                    <text x="230" y="222" textAnchor="middle" fontSize="10.5" fill="#7c3aed" fontWeight="600" fontFamily="inherit">Late converters still earn you commission — up to 30 days</text>
                </g>

            </g>
        </svg>
    );
}
